import { Button, Empty, Select } from "antd";
import { PROFILE_SERVICE_BASE_URL } from "constants";
import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import apiClient from "utils/apiClient";
import {
  LoginOutlined,
  EditOutlined,
  StopOutlined,
  SafetyOutlined,
  SettingOutlined,
  CopyOutlined,
  CheckOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const auditEventIcons = {
  LOGIN: <LoginOutlined />,
  LOGOUT: <LoginOutlined rotate={180} />,
  PROFILE_UPDATE: <EditOutlined />,
  PASSWORD_RESET: <SafetyOutlined />,
  ROLE_ASSIGNED: <SettingOutlined />,
  ACCOUNT_DISABLED: <StopOutlined />,
};
const DefaultAuditIcon = <SettingOutlined />;

const PREVIEW_COUNT = 20;

const auditSeverityColors = {
  INFO: {
    bg: "#E3F2FD",
    text: "#1565C0",
    border: "#BBDEFB",
  },
  WARN: {
    bg: "#FFF8E1",
    text: "#ED6C02",
    border: "#FFE0B2",
  },
  ERROR: {
    bg: "#FDECEA",
    text: "#C62828",
    border: "#F5C6C3",
  },
};

const defaultAuditStyle = {
  bg: "#F3F4F6",
  text: "#374151",
  border: "#E5E7EB",
};

function CopyableValue({ value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  };

  return (
    <span className="copyable" onClick={handleCopy}>
      <span className="copy-icon">
        {copied ? <CheckOutlined /> : <CopyOutlined />}
      </span>
      <span className="copy-text">{value}</span>
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="audit-detail-row">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}

function DetailBlock({ label, value }) {
  return (
    <div className="audit-detail-block">
      <div className="label">{label}</div>
      <pre className="code">{value}</pre>
    </div>
  );
}

function AuditLogRow({ log }) {
  const [expanded, setExpanded] = useState(false);

  const severityStyle = auditSeverityColors[log.severity] || defaultAuditStyle;

  const icon = auditEventIcons[log.eventType] || DefaultAuditIcon;

  return (
    <div
      className={`audit-log-row ${expanded ? "expanded" : ""}`}
      onClick={() => setExpanded((prev) => !prev)}
    >
      {/* header */}
      <div className="audit-log-header">
        <div className="audit-left">
          <span className="audit-icon">{icon}</span>

          <span
            className="audit-badge"
            style={{
              backgroundColor: severityStyle.bg,
              color: severityStyle.text,
              border: `1px solid ${severityStyle.border}`,
            }}
          >
            {log.severity || "INFO"}
          </span>

          <p className="audit-summary">
            {log.summary || "No summary provided"}
          </p>

          {/* meta */}
          <div className="audit-meta">
            <span style={{ fontSize: 10 }}>
              {log.eventType} • {log.sourceService}
            </span>
          </div>
        </div>

        <span className="audit-time">
          {new Date(log.occurredAt).toLocaleString()}
        </span>
      </div>

      {/* expandable details */}
      {expanded && (
        <div className="audit-details" onClick={(e) => e.stopPropagation()}>
          {log.sourceService && (
            <DetailRow label="Source Service" value={log.sourceService} />
          )}
          {log.clientApp && (
            <DetailRow label="Client App" value={log.clientApp} />
          )}
          {log.correlationId && (
            <DetailRow
              label="Correlation ID"
              value={<CopyableValue value={log.correlationId} />}
            />
          )}

          {log.eventCategory && (
            <DetailRow label="Event Category" value={log.eventCategory} />
          )}
          {log.eventType && (
            <DetailRow label="Event Type" value={log.eventType} />
          )}

          {log.changes && (
            <DetailBlock
              label="Changes"
              value={JSON.stringify(log.changes, null, 2)}
            />
          )}

          {log.metadata && (
            <DetailBlock
              label="Metadata"
              value={JSON.stringify(log.metadata, null, 2)}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default function UserAuditLogs({ userId }) {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [last, setLast] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [severityFilter, setSeverityFilter] = useState("ALL");
  const [totalElements, setTotalElements] = useState(0);

  const fetchLogs = async (pageToLoad = 0) => {
    setLoading(true);

    try {
      const res = await apiClient(
        `${PROFILE_SERVICE_BASE_URL}/api/v1/audit/actor/${userId}?page=${pageToLoad}&size=${PREVIEW_COUNT}`,
      );

      setLogs((prev) =>
        pageToLoad === 0 ? res.data.content : [...prev, ...res.data.content],
      );
      setLast(res.data.last);
    } catch (err) {
      console.error("Failed to fetch audit logs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLogs([]);
    setPage(0);
    setCollapsed(true);
    fetchLogs(0);
  }, [userId]);

  const handleViewMore = () => {
    fetchLogs(page + 1);
    setPage((prev) => prev + 1);
  };

  const handleShowLess = () => {
    setPage(0);
    fetchLogs(0);
  };

  // console.log("Logs", logs);
  const filteredLogs =
    severityFilter === "ALL"
      ? logs
      : logs.filter((log) => log.severity === severityFilter);

  // pagination / preview
  //   const visibleLogs = collapsed
  //     ? filteredLogs.slice(0, PREVIEW_COUNT)
  //     : filteredLogs;
  const visibleLogs = filteredLogs;

  return (
    <Card className="custom-shadow">
      <Card.Header className="pb-0 mb-0">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0 fw-bold">Audit Logs</h6>
            <p className="mb-0 pb-0 text-xxs">
              Visibility into actions, changes, and security events for this
              user.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="text-xxs">Severity:</span>
            <Select
              // size="small"
              value={severityFilter}
              onChange={setSeverityFilter}
              style={{ width: 120 }}
            >
              <Option value="ALL">All</Option>
              <Option value="INFO">INFO</Option>
              <Option value="WARN">WARN</Option>
              <Option value="ERROR">ERROR</Option>
            </Select>
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {!loading && filteredLogs.length === 0 && (
          <div className="text-center pb-4 text-muted">
            <Empty
              description="No audit logs found"
              style={{ fontFamily: "Inter", fontSize: 13 }}
            />
          </div>
        )}

        <div className="audit-log-scroll-container">
          {visibleLogs.map((log, index) => (
            <AuditLogRow
              key={
                log.id ||
                log.eventId ||
                log.auditId ||
                (log.correlationId && log.occurredAt
                  ? `${log.correlationId}-${log.occurredAt}`
                  : `${log.eventType || "event"}-${log.occurredAt || "time"}-${index}`)
              }
              log={log}
            />
          ))}
        </div>

        <div className="d-flex gap-2">
          {collapsed && !last && (
            <Button
              onClick={handleViewMore}
              loading={loading}
              style={{ fontSize: 12, marginTop: 10 }}
            >
              View more
            </Button>
          )}

          {logs.length > PREVIEW_COUNT && (
            <Button
              onClick={handleShowLess}
              style={{ fontSize: 12, marginTop: 10 }}
            >
              Show less
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
